import {
  AfterViewInit,
  Component,
  HostBinding,
  Injector,
  Input, NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor, NG_VALIDATORS,
  NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validators
} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "../abstract.component";
import {required} from "@utility";
import {BadgeColor} from "../badge/badge.component";
import {OnScroll, OnScrollService} from '@services';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: SelectComponent,
    multi: true
  },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: SelectComponent
    },]
})
export class SelectComponent extends AbstractComponent implements AfterViewInit, OnInit, OnChanges, ControlValueAccessor, Validators, OnScroll {
  protected readonly SEARCH_ELEMENT_BREAKPOINT = 10;

  @Input() label?: string;
  @Input() cssClass?: string;
  @Input() items?: any[];
  @Input() labels?: any[];
  @Input() translatedLabels?: any[];
  @Input() valueKey?: any;
  @Input() labelKey?: any;
  @Input() labelKeyPrefix?: string;
  @Input() hasDefault: boolean = false;
  @Input() defaultValue?: any = undefined;
  @Input() defaultLabel?: any = "-";
  @Input() placeholder?: string = undefined;
  @Input() autocomplete?: string;
  @Input() fullWidthDropdown: boolean = false;
  @Input() leftAlign: boolean = false;
  @Input() isDropdownListFullWidth: boolean = false;
  @Input() isSmallDropdown: boolean = false;
  @Input() isDarkDropdown: boolean = false;
  @Input() displaySelectionAsBadge: boolean = false;
  @Input() badgeClasses: BadgeColor[] | undefined;
  @Input() closeOnScroll: boolean = false;

  itemLabelArray: { item: any, label: string }[] = [];
  itemClassArray: { item: any, className: BadgeColor }[] = [];
  filteredItemLabelArray: { item: any, label: string }[] = [];
  searchText: string | undefined

  selectedValue?: any;

  onChange = (selectedValue: any) => {
  };

  onTouched: () => void = () => {
  };

  touched: boolean = false;
  disabled: boolean = false;
  invalid: boolean = false;
  errors: ValidationErrors | null = null;
  mouseEntered: boolean = false;

  protected readonly undefined = undefined;

  @HostBinding('class.full-width') get applyFullWidthClass() {
    return this.fullWidthDropdown;
  }

  @ViewChild('dropdownRef') dropdownRef: any;

  closeDropdown() {
    if (this.dropdownRef && typeof this.dropdownRef.close === 'function') {
      this.dropdownRef.close();
    }
  }

  onWindowScroll = () => {
    if (this.closeOnScroll && !this.mouseEntered) {
      this.ngZone.run(() => {
        this.closeDropdown();
      });
    }
  };

  constructor(private translocoService: TranslocoService, private inj: Injector, private ngZone: NgZone, public onScrollService:OnScrollService) {
    super();
  }

  ngOnInit() {
    this.createOptionsLabelsMap();
    this.translocoService.langChanges$.subscribe((value) => {
      this.createOptionsLabelsMap();
    });
    if (this.closeOnScroll) {
      this.onScrollService.addOnScrollListener(this);
    }
  }

  onDropdownMouseLeave() {
    this.mouseEntered = false;
  }

  onDropdownMouseEnter() {
    this.mouseEntered = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']?.currentValue !== changes['items']?.previousValue || changes['values']?.currentValue !== changes['values']?.previousValue) {
      this.createOptionsLabelsMap();
    }
  }

  ngAfterViewInit() {
    const outerControl: AbstractControl | null = this.inj.get(NgControl).control;
    if (outerControl) {
      outerControl.markAsTouched = () => {
        this.touched = true;
      };
      outerControl.markAsUntouched = () => {
        this.touched = false
      };
    }
  }

  writeValue(selectedValue: any) {
    this.selectedValue = selectedValue;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  createOptionsLabelsMap() {
    const itemsArray: any[] = this.items ? [...this.items] : [];
    this.itemLabelArray = []
    this.filteredItemLabelArray = [];
    itemsArray.forEach((itemValue: any, index: number): void => {
      const item = this.valueKey ? itemValue[this.valueKey] : itemValue;
      const label = this.getLabel(itemValue, index);
      this.itemLabelArray.push({item, label})
      if (this.badgeClasses && (this.badgeClasses.length ?? 0 > index) && !!this.badgeClasses.at(index)) {
        const className = this.badgeClasses.at(index) ?? 'red';
        this.itemClassArray.push({item, className});
      }
    });
    this.filteredItemLabelArray = [...this.itemLabelArray];
  }


  private getLabel(item: any, i: number) {
    if (this.labels && this.labels.length > i) {
      return this.translocoService.translate(this.labels[i])
    } else if (this.translatedLabels && this.translatedLabels.length > 0) {
      return this.translatedLabels[i]
    } else if (this.labelKey) {
      return this.labelKeyPrefix ? this.translocoService.translate(this.labelKeyPrefix + (item[this.labelKey] ?? "")) :
        item[this.labelKey]
    } else if (this.labelKeyPrefix) {
      return this.translocoService.translate(this.labelKeyPrefix + item)
    } else return "";
  }

  onSelect(item: any) {
    this.markAsTouched();
    if (!this.disabled) {
      this.selectedValue = item
      this.onChange(this.selectedValue)
    }
  }

  getSelectedValueLabel(): string {
    if (this.hasDefault && this.selectedValue === this.defaultValue) return this.placeholder ?? this.defaultLabel;
    else {
      return this.itemLabelArray.filter((option: { item: any, label: string }) => {
        return option.item === this.selectedValue
      })[0]?.label ?? "";
    }
  }

  getItemClass(): BadgeColor | undefined {
    return this.itemClassArray.filter((option: { item: any, className: BadgeColor }) => {
      return option.item === this.selectedValue
    })[0]?.className;
  }

  onSearch(value: string) {
    this.searchText = value;
    this.filteredItemLabelArray = this.itemLabelArray.filter(item => item.label?.toLowerCase().includes(value?.toLowerCase()))
  }

  dropdownClosed(open: boolean) {
    this.onSearch("");
    this.markAsTouched()
  }

  setDefaultValue() {
    this.onSelect(this.defaultValue)
  }

  onLabelFocus() {
   this.markAsTouched();
  }


  validate(control: AbstractControl): ValidationErrors | null {
    let errors : ValidationErrors | null;
    if ((control.value === undefined || control.value === null) && control.hasValidator(required)) {
      this.invalid = true;
      errors = {
        required: true
      };
    }
    else {
      if (!control.hasError('required') && control.errors) {
        errors = {...control.errors}
        this.invalid = true
      }
      else {
        errors = null;
        this.invalid = false;
      }
    }
    this.errors = {...errors};
    return errors;
  }

  override ngOnDestroy(): void {
    if (this.closeOnScroll) {
      this.onScrollService.removeOnScrollListener(this);
    }
    super.ngOnDestroy();
  }
}
